import { useEffect, useCallback } from "react";

// Fullscreen viewer for gallery media. `items` is the flat post list;
// `index` is the active item (or null when closed).
export default function Lightbox({ items, index, onClose, onNavigate }) {
  const open = index !== null && index >= 0;

  const go = useCallback(
    (dir) => {
      if (!open) return;
      const next = (index + dir + items.length) % items.length;
      onNavigate(next);
    },
    [open, index, items.length, onNavigate]
  );

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, go, onClose]);

  if (!open) return null;
  const item = items[index];

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      onClick={onClose}
    >
      {/* Opaque scrim — a solid layer so the page never bleeds through */}
      <div className="absolute inset-0 bg-[#14100B]" />
      <div className="absolute inset-0 bg-[#14100B]/80 backdrop-blur-md" />

      <button
        aria-label="Close"
        onClick={onClose}
        className="absolute right-4 top-4 z-20 flex h-11 w-11 items-center justify-center rounded-full border border-gold/30 bg-charcoal/60 text-cream/80 transition-colors hover:border-gold hover:text-gold md:right-6 md:top-6"
      >
        <svg viewBox="0 0 24 24" className="h-5 w-5" stroke="currentColor" strokeWidth="1.4" fill="none">
          <path d="M6 6l12 12M18 6L6 18" />
        </svg>
      </button>

      {items.length > 1 && (
        <>
          <NavButton side="left" onClick={(e) => { e.stopPropagation(); go(-1); }} />
          <NavButton side="right" onClick={(e) => { e.stopPropagation(); go(1); }} />
        </>
      )}

      {/* Framed media */}
      <figure
        className="relative z-10 flex max-h-[86vh] max-w-[88vw] flex-col items-center md:max-w-[76vw]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative border border-gold/25 bg-charcoal p-1.5 shadow-2xl shadow-black/60 md:p-2">
          {item.type === "video" ? (
            <video
              src={item.src}
              poster={item.poster}
              controls
              autoPlay
              loop
              playsInline
              className="block max-h-[76vh] w-auto max-w-full"
            />
          ) : (
            <img
              src={item.src}
              alt=""
              className="block max-h-[76vh] w-auto max-w-full object-contain"
            />
          )}
        </div>

        {items.length > 1 && (
          <figcaption className="mt-4 font-sans text-xs uppercase tracking-[0.3em] text-cream/50">
            {index + 1} / {items.length}
          </figcaption>
        )}
      </figure>
    </div>
  );
}

function NavButton({ side, onClick }) {
  return (
    <button
      aria-label={side === "left" ? "Previous" : "Next"}
      onClick={onClick}
      className={`absolute top-1/2 z-20 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-gold/30 bg-charcoal/60 text-cream/80 transition-colors hover:border-gold hover:text-gold ${
        side === "left" ? "left-3 md:left-8" : "right-3 md:right-8"
      }`}
    >
      <svg viewBox="0 0 24 24" className="h-6 w-6" stroke="currentColor" strokeWidth="1.2" fill="none">
        {side === "left" ? <path d="M15 5l-7 7 7 7" /> : <path d="M9 5l7 7-7 7" />}
      </svg>
    </button>
  );
}
